import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import clsx from 'clsx';
import styles from './Button.module.scss';
import PulseLoader from 'react-spinners/PulseLoader';

export type ButtonVariant = 'primary' | 'green' | 'black' | 'danger' | 'outlined' | 'text-green';

export type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
    label: string | React.ReactNode;
    fullWidth?: boolean;
    size?: 'small' | 'medium' | 'large' | 'inline';
    noColorOnDisabled?: boolean;
    onlyIcon?: boolean;
    variant?: ButtonVariant;
    isLoading?: boolean;
    leftIcon?: IconProp;
    rightIcon?: IconProp;
    className?: string;
};

const SPINNER_COLORS: Record<ButtonVariant, string> = {
    'primary': '#ffffff',
    'green': '#ffffff',
    'black': '#ffffff',
    'danger': '#ffffff',
    'outlined': '#000000',
    'text-green': '#92b4a7'
};

const Button: React.FC<ButtonProps> = props => {
    const {
        size = 'medium',
        variant = 'primary',
        fullWidth = false,
        label,
        leftIcon,
        rightIcon,
        isLoading,
        onlyIcon,
        noColorOnDisabled,
        ...buttonProps
    } = props;

    const spinner = (
        <div className={styles.spinner}>
            <PulseLoader
                size={5}
                loading={true}
                color={SPINNER_COLORS[variant]}
            />
        </div>
    );

    return (
        <button
            {...buttonProps}
            data-only-icon={onlyIcon}
            data-full-width={fullWidth}
            data-no-color-on-disabled={noColorOnDisabled}
            className={clsx(styles.root, props.className, {
                [styles.small]: size === 'small',
                [styles.medium]: size === 'medium',
                [styles.large]: size === 'large',
                [styles.inline]: size === 'inline',
                [styles.loading]: isLoading,
                [styles.primary]: variant === 'primary',
                [styles.green]: variant === 'green',
                [styles.black]: variant === 'black',
                [styles.danger]: variant === 'danger',
                [styles.outlined]: variant === 'outlined',
                [styles.textGreen]: variant === 'text-green'
            })}
        >
            {leftIcon && <FontAwesomeIcon icon={leftIcon} />}

            {!onlyIcon && label}

            {rightIcon && <FontAwesomeIcon icon={rightIcon} />}

            {isLoading && spinner}
        </button>
    );
};

export default Button;
